import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GaussianSplattingRenderer } from './renderer/GaussianSplattingRenderer';
import { WebXRController } from './xr/WebXRController';
import { UIManager } from './ui/UIManager';
import { Stats } from './utils/Stats';
import { SparkRenderer } from '@sparkjsdev/spark';

/**
 * Main application class for WebXR 3DGS Viewer
 */
class WebXRViewer {
  private canvas: HTMLCanvasElement;
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private cameraRig: THREE.Group; // VR camera rig for movement
  private gsSplattingRenderer: GaussianSplattingRenderer | null = null;
  private xrController: WebXRController;
  private uiManager: UIManager;
  private stats: Stats;
  private orbitControls: OrbitControls;
  private animationId: number | null = null;
  private lastFrameTime: number = performance.now();
  private frameCount: number = 0;

  constructor() {
    this.canvas = document.getElementById('canvas') as HTMLCanvasElement;
    if (!this.canvas) {
      throw new Error('Canvas element not found');
    }

    // Initialize Three.js renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: false, // 3dgs does not benefit from antialiasing
      alpha: true,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setClearColor(0x1a1a1a, 1);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1;

    // Initialize scene and camera
    this.scene = new THREE.Scene();
    const spark = new SparkRenderer({
      renderer: this.renderer,
      maxStdDev: Math.sqrt(5),
    });
    this.scene.add(spark);
    this.scene.background = new THREE.Color(0x1a1a1a);

    const aspectRatio = window.innerWidth / window.innerHeight;
    this.camera = new THREE.PerspectiveCamera(75, aspectRatio, 0.1, 1000);
    this.camera.position.set(0, 0, 5);
    this.camera.lookAt(0, 0, 0);

    // Create VR camera rig
    // The rig acts as a parent for the camera, allowing gamepad controls to move
    // the entire play space while the camera itself is controlled by the headset
    this.cameraRig = new THREE.Group();
    this.cameraRig.position.set(0, 0, 0);
    this.scene.add(this.cameraRig);

    // Add camera to the rig (it will be parented in VR mode)
    // Note: Camera will be temporarily removed from rig when not in VR
    // to allow orbit controls to work properly

    // Initialize components
    this.stats = new Stats();
    this.uiManager = new UIManager();
    this.orbitControls = new OrbitControls(this.camera, this.canvas);
    this.orbitControls.autoRotate = false;
    this.orbitControls.enableDamping = true;
    this.orbitControls.dampingFactor = 0.05;
    this.xrController = new WebXRController(this.renderer, this.scene, this.camera, this.cameraRig);

    this.setupEventListeners();
    this.uiManager.setStatus('Ready. Load a model to begin.');
    this.animate();
  }

  /**
   * Setup UI event listeners
   */
  private setupEventListeners(): void {
    window.addEventListener('resize', () => this.onWindowResize());

    const btnLoadModel = document.getElementById('btn-load-model');
    const btnToggleVR = document.getElementById('btn-toggle-vr');
    const btnResetView = document.getElementById('btn-reset-view');
    const btnFullscreen = document.getElementById('btn-fullscreen');

    btnLoadModel?.addEventListener('click', () => this.loadModel());
    btnResetView?.addEventListener('click', () => this.resetView());
    btnFullscreen?.addEventListener('click', () => this.toggleFullscreen());

    const splatCountSelect = document.getElementById('splat-count') as HTMLSelectElement;
    splatCountSelect?.addEventListener('change', (e) => {
      const count = parseInt((e.target as HTMLSelectElement).value);
      this.setSplatCount(count);
    });

    btnToggleVR?.addEventListener('click', async () => {
      try {
        await this.xrController.toggleVR();
      } catch (e) {
        console.error('Failed to toggle VR:', e);
        this.uiManager.addMessage('Failed to toggle VR', 'error');
      }
    });

    // Check for WebXR support and update UI
    this.checkWebXRSupport();
  }

  /**
   * Check WebXR support and display appropriate buttons
   */
  private async checkWebXRSupport(): Promise<void> {
    const btnToggleVR = document.getElementById('btn-toggle-vr') as HTMLButtonElement;
    const btnXRVR = document.getElementById('btn-xr-vr') as HTMLButtonElement;
    const btnXRAR = document.getElementById('btn-xr-ar') as HTMLButtonElement;
    const xrStatusElement = document.getElementById('xr-status');

    if (!navigator.xr) {
      this.uiManager.addMessage('WebXR not supported on this device', 'warning');
      if (xrStatusElement) xrStatusElement.textContent = '✗';
      return;
    }

    if (xrStatusElement) xrStatusElement.textContent = '✓';

    try {
      const isVRSupported = await navigator.xr.isSessionSupported('immersive-vr');
      if (isVRSupported) {
        if (btnToggleVR) btnToggleVR.style.display = 'block';
        // Keep legacy VR button hidden to avoid duplication
        if (btnXRVR) btnXRVR.style.display = 'none';
      }
    } catch (e) {
      console.warn('VR not supported');
    }

    try {
      const isARSupported = await navigator.xr.isSessionSupported('immersive-ar');
      if (isARSupported && btnXRAR) {
        btnXRAR.style.display = 'block';
      }
    } catch (e) {
      console.warn('AR not supported');
    }
  }

  /**
   * Set the maximum splat count
   */
  private setSplatCount(count: number): void {
    if (this.gsSplattingRenderer) {
      this.gsSplattingRenderer.setSplatCount(count);
      this.uiManager.addMessage(`Max splats set to ${count.toLocaleString()}`, 'info');
    }
  }

  /**
   * Public method to load demo model
   */
  public async loadDemoModel(): Promise<void> {
    await this.loadModel();
  }

  /**
   * Load a 3DGS model from URL or demo
   */
  private async loadModel(): Promise<void> {
    const urlInput = document.getElementById('model-url') as HTMLInputElement;
    const url = urlInput?.value?.trim();

    this.uiManager.setStatus('Loading model...');
    this.uiManager.setLoadingProgress(0);

    const onProgress = (perc: number) => {
      this.uiManager.setLoadingProgress(perc);
    };

    let loadedSuccessfully = false;

    try {
      // Initialize the Gaussian Splatting renderer
      if (!this.gsSplattingRenderer) {
        this.gsSplattingRenderer = new GaussianSplattingRenderer(this.scene, this.renderer);
      }

      // Load from URL if provided, otherwise load demo
      if (url) {
        await this.gsSplattingRenderer.loadFromURL(url, onProgress);
      } else {
        await this.gsSplattingRenderer.loadDemo(onProgress);
      }

      this.uiManager.setLoadingProgress(1);
      this.uiManager.setStatus('Model loaded successfully!');

      // Display splat metadata
      const metadata = this.gsSplattingRenderer.getSplatMetadata();
      if (metadata) {
        console.log('Splat Metadata:', metadata);
        this.uiManager.addMessage(
          `Loaded ${metadata.numSplats.toLocaleString()} splats (max: ${metadata.maxSplats.toLocaleString()})`,
          'success'
        );
      } else {
        this.uiManager.addMessage('Model ready for visualization', 'success');
      }

      this.resetView();
      loadedSuccessfully = true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.uiManager.setStatus('Failed to load model');
      this.uiManager.addMessage(`Error: ${message}`, 'error');
      console.error('Failed to load model:', error);
    } finally {
      // Ensure status is at least set back to base text when done if nothing loaded
      if (
        !loadedSuccessfully &&
        (!this.gsSplattingRenderer || this.gsSplattingRenderer.getSplatCount() === 0)
      ) {
        this.uiManager.setStatus('Ready. Load a model to begin.');
      }
    }
  }

  /**
   * Reset camera view
   */
  private resetView(): void {
    this.camera.position.set(0, 0, 5);
    this.camera.lookAt(0, 0, 0);
    this.orbitControls.reset();
  }

  /**
   * Toggle fullscreen
   */
  private toggleFullscreen(): void {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.warn('Fullscreen request failed:', err);
      });
    } else {
      document.exitFullscreen().catch((err) => {
        console.warn('Exit fullscreen failed:', err);
      });
    }
  }

  /**
   * Handle window resize
   */
  private onWindowResize(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  /**
   * Animation loop
   */
  private animate = (): void => {
    this.animationId = requestAnimationFrame(this.animate);

    const now = performance.now();
    const deltaTime = (now - this.lastFrameTime) / 1000;
    this.lastFrameTime = now;
    this.frameCount++;

    // Update controls (disabled during XR sessions)
    if (!this.xrController.isSessionActive()) {
      this.orbitControls.update();
    }

    // Render scene
    if (this.xrController.isSessionActive()) {
      // WebXR rendering is handled by XR controller
      this.xrController.render(deltaTime);
    } else {
      this.renderer.render(this.scene, this.camera);
    }

    // Update stats
    this.stats.update();
    this.updateStatsDisplay();
  };

  /**
   * Update stats display
   */
  private updateStatsDisplay(): void {
    const fpsElement = document.getElementById('fps');
    const splatsElement = document.getElementById('splats');

    if (fpsElement) {
      fpsElement.textContent = this.stats.fps.toString();
    }

    if (splatsElement && this.gsSplattingRenderer) {
      splatsElement.textContent = this.gsSplattingRenderer.getSplatCount().toString();
    }
  }

  /**
   * Cleanup resources
   */
  public dispose(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
    }
    this.gsSplattingRenderer?.dispose();
    this.xrController.dispose();
    this.orbitControls.dispose();
    this.renderer.dispose();
  }
}

// Initialize application when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  try {
    const app = new WebXRViewer();
    (window as any).webxrViewer = app;

    // Auto-load the default butterfly.spz model when the page is ready
    // Give it a small delay to ensure everything is fully rendered
    setTimeout(() => {
      console.log('Auto-loading default butterfly.spz model...');
      app.loadDemoModel().catch((error) => {
        console.error('Failed to auto-load demo model:', error);
      });
    }, 500);
  } catch (error) {
    console.error('Failed to initialize WebXR Viewer:', error);
    const messagesDiv = document.getElementById('messages');
    if (messagesDiv) {
      messagesDiv.innerHTML =
        '<div class="error">Failed to initialize application. Check console for details.</div>';
    }
  }
});

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  const app = (window as any).webxrViewer;
  if (app) {
    app.dispose();
  }
});
